import { useCallback, useMemo, useState } from 'react';
import { Button, Skeleton } from 'antd';
import { controItemExtranDom } from '@/pages/service/massage-setting/utils';
import { isNewSoftwareVersion, Toast } from '@/utils';
import service from '@/service';
import { ALL_PAGES } from '@/constant';
import { useNav } from '@/hooks';
import cx from './index.module.scss';

const TextCard = ({
  title,
  text,
  unit,
}: {
  title: string | number;
  text: string | number | undefined | React.ReactNode;
  unit: string | React.ReactNode;
}) => {
  const handleTitle = (title: string | number) => {
    if (title === 0) {
      return `${title}`;
    }
    return title ? `${title}` : '-';
  };
  return (
    text && (
      <div className={cx('text')}>
        <div className={cx('label')}>{text}</div>
        <div className={cx('value')}>
          <div>{handleTitle(title)}</div>
          <div className={cx({ hide: handleTitle(title) === '-' })}>{unit}</div>
        </div>
      </div>
    )
  );
};

const Unit = {
  Time: '分钟',
  Height: 'cm',
  // 总打击次数
  TotalHits: '次',
  Force: 'N',
  PainPoint: '个',
  // 痛点加强次数
  PainPointReinforceNumber: '次',
};

// 传入的mm需要展示cm
const HeightMMToCM = (height?: number) => {
  return Math.round(height / 10);
};

export const PhysiotherapyParameter = ({
  serviceDetail,
  massageTaskInfoList,
  isGettingData,
}: {
  isGettingData: boolean;
  serviceDetail: GetServiceOrderDetailResponse;
  massageTaskInfoList: GetMassageTaskInfoResponse[];
}) => {
  const nav = useNav();
  const [isOneMoreOrderLoading, setIsOneMoreOrderLoading] = useState<boolean>(false);
  const RenderDetail = useCallback(
    (param: GetMassageTaskInfoResponse, type: Massage.MassageType) => {
      const massageItem = param.massageItem;
      // 专门用来存储详情展示的字段
      const taskParam = param?.taskParam;
      if (!taskParam) {
        return <></>;
      }

      const { shockwave, radioFrequency, massage, moxibustion, hotStone } = taskParam;

      if (massageItem === 'Shockwave') {
        return type === 'ManualMassage' ? (
          <div className={cx('shockwave-params-box', { 'extra-shockwave-manual': massageTaskInfoList?.length > 1 })}>
            <TextCard text="人工打击次数" title={shockwave?.totalHits} unit={Unit.TotalHits} />
            <TextCard
              text="人工理疗强度"
              title={shockwave?.power}
              unit={controItemExtranDom['shockwave.power']?.suffix}
            />

            <TextCard
              text="人工理疗频率"
              title={shockwave?.frequency}
              unit={controItemExtranDom['shockwave.frequency']?.suffix}
            />
          </div>
        ) : (
          <>
            <div className={cx('shockwave-params-box')}>
              <TextCard
                title={shockwave?.lineHits}
                unit={controItemExtranDom['shockwave.basic_hits']?.suffix}
                text="走线打击次数"
              />

              <TextCard
                title={shockwave?.noduleHits}
                unit={controItemExtranDom['shockwave.nodule_hits']?.suffix}
                text="痛点打击次数"
              />

              {/* <TextCard title={shockwave?.totalHits} text="总打击次数" unit={Unit.TotalHits} /> */}

              <TextCard title={shockwave?.power} unit={controItemExtranDom['shockwave.power']?.suffix} text="强度" />

              <TextCard title={shockwave?.force} unit={controItemExtranDom['robot.force']?.suffix} text="力度" />

              <TextCard
                title={shockwave?.frequency}
                unit={controItemExtranDom['shockwave.frequency']?.suffix}
                text="频率"
              />
            </div>
          </>
        );
      }
      // 艾灸
      if (massageItem === 'Moxibustion') {
        return (
          <div className={cx('moxibustion-params-box')}>
            {/* //TODO： 这里需要优化为一个config */}
            {moxibustion?.acupointAreas && (
              <TextCard text="机器人理疗穴区" title={moxibustion?.acupointAreas?.join('、')} unit="" />
            )}
            {/* 非矩阵艾灸 只有时长和力度 */}
            {moxibustion?.height ? (
              <TextCard
                text="机器人理疗高度"
                title={HeightMMToCM(moxibustion?.height)}
                unit={controItemExtranDom['robot.height']?.suffix}
              />
            ) : (
              <TextCard
                text="机器人理疗力度"
                title={moxibustion?.force}
                unit={controItemExtranDom['robot.force']?.suffix}
              />
            )}
          </div>
        );
      }

      if (massageItem === 'RadioFrequency') {
        return type === 'Massage' ? (
          <div className={cx('rf-massage-wrapper')}>
            <TextCard
              text="机器人理疗力度"
              title={radioFrequency?.force}
              unit={controItemExtranDom['robot.force']?.suffix}
            />
            <TextCard
              text="机器人理疗能量"
              title={Math.round(radioFrequency?.power * 100)}
              unit={controItemExtranDom['radio_frequency.power']?.suffix}
            />
          </div>
        ) : (
          <TextCard
            text="人工理疗能量"
            title={Math.round(radioFrequency?.power * 100)}
            unit={controItemExtranDom['radio_frequency.power']?.suffix}
          />
        );
      }
      if (massageItem === 'Massage') {
        return (
          <div className={cx('params-box')}>
            <TextCard text="机器人理疗力度" title={massage?.force} unit={Unit.Force} />
            {/* Note:1.6.10以下没有痛点加强次数 */}
            {isNewSoftwareVersion(serviceDetail?.deviceSoftwareVersion, '1.6.10') && (
              <>
                <TextCard text="手诊痛点个数" title={massage?.noduleCount} unit={Unit.PainPoint} />
                <TextCard
                  text="痛点加强次数"
                  title={massage?.intensifyTreatmentCount}
                  unit={Unit.PainPointReinforceNumber}
                />
              </>
            )}
          </div>
        );
      }

      if (massageItem === 'HotStone') {
        return (
          <div className={cx('params-box')}>
            {type !== 'ManualMassage' && (
              <TextCard
                text="机器人理疗力度"
                title={hotStone?.force}
                unit={controItemExtranDom['robot.force']?.suffix}
              />
            )}

            <TextCard
              text={`${type === 'ManualMassage' ? '人工' : '机器人'}理疗温度`}
              title={hotStone?.temperatureLevel}
              unit={controItemExtranDom['hot_stone.temperature_level']?.suffix}
            />
            <TextCard
              text={`${type === 'ManualMassage' ? '人工' : '机器人'}理疗电流`}
              title={Math.round(hotStone?.power * 100)}
              unit={controItemExtranDom['hot_stone.power']?.suffix}
            />
          </div>
        );
      }

      return <></>;
    },
    [massageTaskInfoList?.length, serviceDetail?.deviceSoftwareVersion]
  );

  const isManualRadioFrequency = useMemo(() => {
    return serviceDetail?.onlyManual && serviceDetail?.itemType === 'RadioFrequency';
  }, [serviceDetail?.itemType, serviceDetail?.onlyManual]);

  const handleOneMoreOrder = useCallback(async () => {
    try {
      setIsOneMoreOrderLoading(true);
      const { data } = await service.service.checkStoreGoods(serviceDetail?.goodsId);
      if (!!data) {
        nav(ALL_PAGES.ADD_SERVICE, {
          state: {
            orderId: serviceDetail?.serviceOrderId,
            massageItem: serviceDetail?.itemType,
            bodyPart: serviceDetail?.bodyPart,
            storeGoodsId: serviceDetail?.goodsId,
          },
        });
      } else {
        Toast.fail('该方案已下架，请重新下单');
      }
    } finally {
      setIsOneMoreOrderLoading(false);
    }
  }, [nav, serviceDetail]);

  console.log(massageTaskInfoList);

  return (
    <div className={cx('wrapper', { 'manual-rf': isManualRadioFrequency })}>
      <Skeleton loading={isGettingData} paragraph={{ rows: 1 }} title={false}>
        <div className={cx('title')}>理疗参数</div>
        <div className={cx('setting-params-wrapper')}>
          {massageTaskInfoList?.map(item => (
            <div key={item?.taskId} className={cx(serviceDetail?.itemType)}>
              {RenderDetail(item, item?.massageType)}
            </div>
          ))}
        </div>
        {serviceDetail?.itemType === 'Moxibustion' && serviceDetail?.hasSetting && (
          <Button
            loading={isOneMoreOrderLoading}
            color="primary"
            className={cx('one-more-order-btn')}
            onClick={handleOneMoreOrder}
          >
            再次服务
          </Button>
        )}
      </Skeleton>
    </div>
  );
};
